// storefront.ts — public seller storefront SPA (/s/<slug>). Truth-first: every
// used/refurb listing renders its disclosed faults UP FRONT, before the Buy.

import { esc } from './ui.js';

const app = document.getElementById('app')!;

interface Fault { category?: string; severity?: string; title: string; detail: string; }
interface Listing { id: string; name: string; brand: string | null; description: string | null; price_usd: string; currency: string; condition: string; type: string; cover_image: string | null; category: string | null; faults: Fault[]; }
interface Store { account: { slug: string; name: string; tagline: string | null; logo_url: string | null }; count: number; listings: Listing[]; }

const COND: Record<string, string> = { new: 'New', refurb: 'Refurbished', open_box: 'Open box', used: 'Used', renewed: 'Renewed' };
function money(s: string, c = 'USD'): string { const n = Number(s); return (c === 'USD' ? '$' : '') + (isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : s); }
function slug(): string { const p = location.pathname.split('/').filter(Boolean); return p[1] ? decodeURIComponent(p[1]) : ''; }

async function render(): Promise<void> {
  const s = slug();
  if (!s) { app.innerHTML = `<div class="loading">No storefront specified.</div>`; return; }
  let store: Store;
  try { const r = await fetch(`/api/storefront/${encodeURIComponent(s)}`); if (!r.ok) throw 0; store = await r.json(); }
  catch { app.innerHTML = `<div class="loading">Storefront <b>${esc(s)}</b> not found. <a href="/shop">Back to shop</a></div>`; return; }

  const banner = new URLSearchParams(location.search).get('status');
  app.innerHTML = `
    ${banner === 'paid' ? `<div class="sf-banner ok">✓ Payment complete — thank you. Your receipt is on the way.</div>` : ''}
    ${banner === 'cancelled' ? `<div class="sf-banner warn">Checkout cancelled. No charge was made.</div>` : ''}
    <header class="sf-head">
      <div class="sf-logo">${store.account.logo_url ? `<img src="${esc(store.account.logo_url)}" alt="">` : esc(store.account.name.slice(0, 1).toUpperCase())}</div>
      <div>
        <h1>${esc(store.account.name)}</h1>
        ${store.account.tagline ? `<p class="sf-tag">${esc(store.account.tagline)}</p>` : ''}
        <span class="sf-badge">🛡 Honest storefront · ${store.count} listing${store.count === 1 ? '' : 's'} · faults disclosed up front</span>
      </div>
    </header>
    ${store.listings.length ? `<div class="sf-grid">${store.listings.map(card).join('')}</div>`
      : `<div class="loading">This seller hasn't listed anything yet.</div>`}`;

  app.querySelectorAll<HTMLButtonElement>('[data-buy]').forEach(btn => {
    btn.addEventListener('click', () => buy(store.account.slug, btn.dataset.buy!, btn));
  });
}

function card(l: Listing): string {
  const used = l.condition !== 'new';
  const faults = l.faults || [];
  return `
  <article class="sf-card">
    <div class="sf-img">${l.cover_image ? `<img src="${esc(l.cover_image)}" alt="" loading="lazy" onerror="this.style.display='none'">` : `<span>${esc(l.brand || l.name.slice(0, 12))}</span>`}</div>
    <div class="sf-body">
      <div class="sf-cond cond cond-${esc(l.condition)}">${esc(COND[l.condition] || l.condition)}</div>
      <h3>${esc(l.name)}</h3>
      ${l.description ? `<p class="sf-desc">${esc(l.description)}</p>` : ''}
      ${used ? (faults.length ? `
        <div class="sf-faults">
          <div class="sf-faults-h">🚩 Disclosed up front (${faults.length})</div>
          <ul>${faults.map(f => `<li class="sev-${esc(f.severity || 'minor')}"><span class="fdot"></span><div><b>${esc(f.title)}</b><p>${esc(f.detail)}</p></div></li>`).join('')}</ul>
        </div>`
        : `<div class="sf-faults clean">✓ Seller reports no faults on this ${esc(COND[l.condition] || l.condition).toLowerCase()} item.</div>`) : ''}
      <div class="sf-foot">
        <span class="sf-price">${money(l.price_usd, l.currency)}</span>
        <button class="btn btn-primary sf-buy" data-buy="${esc(l.id)}">Buy now</button>
      </div>
    </div>
  </article>`;
}

async function buy(account: string, productId: string, btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true; const orig = btn.textContent; btn.textContent = '…';
  try {
    const r = await fetch(`/${encodeURIComponent(account)}/test/api/orders`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ product_id: productId }),
    });
    const d = await r.json();
    if (d.checkout_url) { location.href = d.checkout_url; return; }
    if (d.disabled || d.gated) { alert(d.message || 'Checkout isn’t open yet — this storefront is in preview.'); }
    else { alert(d.error || 'Could not start checkout.'); }
  } catch { alert('Could not start checkout.'); }
  btn.disabled = false; btn.textContent = orig;
}

addEventListener('popstate', render);
render();
